// Types
import { UserRole } from 'api/graphql-global-types';
import type { MerchShoppingCart } from 'context/AppContext';

export const REDIRECT = 'redirect';
export const STORE_SOCIAL_SIGNUP_ROLE = 'store-socail-signup';
export const MERCH_SHOPPING_CART = 'merch-shopping-cart';
export const RTMP_STREAM_ID = 'rtmp-stream-id';
export const SIGN_UP_INVITE_CODE = 'sign-up-invite-code';
export const PPV_BUY_STREAM_ID = 'ppv-buy-stream-id';
export const CREATOR_SIGN_UP_FLOW = 'creator-sign-up-flow';
export const MATCHUP_INVITE_TOKEN = 'matchup-invite-token';

const setItem = (key: string, value: string): void => {
  typeof window !== 'undefined' && localStorage.setItem(key, value);
};

const getItem = (key: string): string | null => {
  return typeof window !== 'undefined' ? localStorage.getItem(key) : null;
};

const removeItem = (key: string): void => {
  typeof window !== 'undefined' && localStorage.removeItem(key);
};

// Redirected from
export const setRedirectToLocalStorage = (path: string): void => {
  setItem(REDIRECT, path);
};

export const getRedirectFromLocalStorage = (): string | null => {
  return getItem(REDIRECT);
};

export const clearRedirectFromLocalStorage = (): void => {
  removeItem(REDIRECT);
};

// Full Onboard (Go Pro)
export const setFullOnboardingLocalStorageFlag = (flag: string): void => {
  setItem('fullOnboarding', flag);
};

export const clearFullOnboardingLocalStorageFlag = (): void => {
  removeItem('fullOnboarding');
};

export const getFullOnboardingLocalStorageFlag = (): string | null => {
  return getItem('fullOnboarding');
};

// Store social sign-up role
export const setStoreSocialSignupToLocalStorage = (role: UserRole): void => {
  setItem(STORE_SOCIAL_SIGNUP_ROLE, role);
};

export const getStoreSocialSignupFromLocalStorage = (): string | null => {
  return getItem(STORE_SOCIAL_SIGNUP_ROLE);
};

export const clearStoreSocialSignupFromLocalStorage = (): void => {
  removeItem(STORE_SOCIAL_SIGNUP_ROLE);
};

// Shopping cart
export const addMerchShoppingCartToLocalStorage = (
  merchShoppingCart: MerchShoppingCart
): void => {
  const newShoppingCartItems = JSON.stringify(merchShoppingCart);

  setItem(MERCH_SHOPPING_CART, newShoppingCartItems);
};

export const getMerchShoppingCartFromLocalStorage = (): MerchShoppingCart => {
  const shoppingCart = getItem(MERCH_SHOPPING_CART);

  if (shoppingCart) {
    return JSON.parse(shoppingCart);
  }

  return [];
};

export const clearMerchShoppingCartFromLocalStorage = (): void => {
  removeItem(MERCH_SHOPPING_CART);
};

// RTMP
export const setRtmpStreamIdToLocalStorage = (id: string): void => {
  setItem(RTMP_STREAM_ID, id);
};

export const getRtmpStreamIdFromLocalStorage = (): string | null => {
  return getItem(RTMP_STREAM_ID);
};

export const clearRtmpStreamIdFromLocalStorage = (): void => {
  removeItem(RTMP_STREAM_ID);
};

// Sign Up Invite
export const setSignUpInviteCodeToLocalStorage = (code: string): void => {
  setItem(SIGN_UP_INVITE_CODE, code);
};

export const clearSignUpInviteCodeFromLocalStorage = (): void => {
  removeItem(SIGN_UP_INVITE_CODE);
};

export const getSignUpInviteCodeFromLocalStorage = (): string | null => {
  return getItem(SIGN_UP_INVITE_CODE);
};

export const setPpvBuyStreamIdToLocalStorage = (streamId: string): void => {
  setItem(PPV_BUY_STREAM_ID, streamId);
};

export const getPpvBuyStreamIdFromLocalStorage = (): string | null => {
  return getItem(PPV_BUY_STREAM_ID);
};

export const clearPpvBuyStreamIdFromLocalStorage = (): void => {
  removeItem(PPV_BUY_STREAM_ID);
};

export const setCreatorSignUpFlowLocalStorageFlag = (flag: string): void => {
  setItem(CREATOR_SIGN_UP_FLOW, flag);
};

export const getCreatorSignUpFlowLocalStorageFlag = (): string | null => {
  return getItem(CREATOR_SIGN_UP_FLOW);
};

export const clearCreatorSignUpFlowLocalStorageFlag = (): void => {
  removeItem(CREATOR_SIGN_UP_FLOW);
};

// Fight-card invite token, kept until the invitee finishes onboarding and the invite is accepted.
// Expires so an abandoned sign-up can't be picked up by a later session in the same browser.
export const MATCHUP_INVITE_TOKEN_TTL_MS = 24 * 60 * 60 * 1000;

export const setMatchupInviteTokenToLocalStorage = (token: string): void => {
  setItem(MATCHUP_INVITE_TOKEN, JSON.stringify({ token, savedAt: Date.now() }));
};

export const getMatchupInviteTokenFromLocalStorage = (): string | null => {
  const saved = getItem(MATCHUP_INVITE_TOKEN);

  if (!saved) {
    return null;
  }

  try {
    const { token, savedAt } = JSON.parse(saved);

    if (
      typeof token === 'string' &&
      Date.now() - savedAt < MATCHUP_INVITE_TOKEN_TTL_MS
    ) {
      return token;
    }
  } catch {
    // Malformed value, dropped below.
  }

  removeItem(MATCHUP_INVITE_TOKEN);
  return null;
};

export const clearMatchupInviteTokenFromLocalStorage = (): void => {
  removeItem(MATCHUP_INVITE_TOKEN);
};
